import { PresenterCheckButton } from '../components/PresenterCheckButton';
import { useMemo, useRef, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Activity,
  Archive,
  ArchiveRestore,
  Ban,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  Download,
  RefreshCw,
  Repeat2,
  RotateCcw,
  Search,
  Send,
  Sparkles,
  Tags,
  Trash2,
  Upload,
  UserPlus,
  UserRoundCog,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Pill } from '@/components/ui/pill';
import { RowActions } from '@/components/ui/row-actions';
import { useUrlState } from '@/hooks/useUrlState';
import { useDiscoveryClient } from '../api/context';
import type { Folder, LeadMarkAction } from '../api/types';
import { foldersKey } from '../hooks/folders';
import { useUpdateLead } from '../hooks/leads';
import { useRefreshStatuses } from '../hooks/bridge';
import { activeJobKey, useExportCsv, useImportCsv } from '../hooks/scrape';
import { useSchedules } from '../hooks/schedules';
import { SearchPanel } from '../screens/SearchPanel';
import { SchedulePanel } from '../components/SchedulePanel';
import { FolderGuidancePanel } from '../components/FolderGuidance';
import { OutcomeChips, SearchQualityNotice } from '../components/OutcomeChips';
import { useOutcomes } from '../suggestions/hooks';
import { folderQualityNotice } from '../suggestions/model';
import { LookalikeDialog } from '../components/LookalikeDialog';
import { RepostDialog } from '../components/RepostDialog';
import { AddCreatorsDialog } from '../components/AddCreatorsDialog';
import { LeadTable, BulkActions, type BulkAction } from '../components/lead-table/LeadTable';
import { LeadFilters } from '../components/lead-table/LeadFilters';
import { CreatorSheet, type SheetAction } from '../components/lead-table/CreatorSheet';
import { fromLead, formatDate, type LeadRow } from '../components/lead-table/rows';
import {
  LEAD_URL_DEFAULTS,
  LEADS_PAGE_SIZE,
  toLeadFilter,
  toLeadQuery,
} from '../components/lead-table/leadFilterModel';
import {
  emptySelection,
  filterKeyOf,
  forFilter,
  selectedCount,
  type SelectionState,
} from '../components/lead-table/selection';
import { PLATFORM_LABELS, type Platform } from '../lib/platform';
import { FOLDER_TABS, FOLDER_TAB_LABELS, useDiscoveryPaths, type FolderTab } from './paths';
import {
  FOLDER_PURPOSE_LABELS,
  FOLDER_STATUS_LABELS,
  archivesInDays,
  archivesInLabel,
  folderPurpose,
  folderStatus,
  openLeadCount,
  ownerOf,
  totalLeadCount,
} from './folderModel';
import { collectAll, useArchiveAfterCompleteDays, useFolderLeadsPage, useMarkLeads, useRemoveLeads } from './hooks';
import { ReassignDialog, SendToOutreachDialog, type BulkTarget } from './AssignDialogs';
import { useFolderMenu } from './FolderDialogs';
import { FolderProgressBar } from './FolderProgressBar';
import { FolderActivity } from './FolderActivity';
import { FolderDot, MenuButton, Note } from './ui';

type DialogId = null | 'send' | 'reassign' | 'reassignOpen' | 'lookalike' | 'reposts' | 'add' | 'remove';

const MARK_DONE_TEXT: Record<LeadMarkAction, string> = {
  irrelevant: 'marked irrelevant',
  complete: 'marked complete',
  reopen: 'reopened',
};

/**
 * One folder: header (status, progress, actions) and tabs. The Leads tab's
 * filters, sort and page live in the URL; the selection lives here so the
 * header's "Send to outreach" sends exactly what the table shows selected.
 */
export function FolderWorkspace({ folder, tab }: { folder: Folder; tab: FolderTab }): JSX.Element {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const paths = useDiscoveryPaths();
  const archiveAfterDays = useArchiveAfterCompleteDays();
  const menu = useFolderMenu(folder, { onDeleted: () => navigate(paths.home) });

  // ── Leads: URL filters → server query ────────────────────────────────────
  const [url, setUrl] = useUrlState(LEAD_URL_DEFAULTS);
  const filter = useMemo(() => toLeadFilter(url), [url]);
  const query = useMemo(() => toLeadQuery(url), [url]);
  const filterKey = `${folder.publicId}|${filterKeyOf(filter)}`;
  const leadsQ = useFolderLeadsPage(folder.publicId, query, tab === 'leads');
  const rows = useMemo(() => (leadsQ.data?.rows ?? []).map(fromLead), [leadsQ.data]);
  const total = leadsQ.data?.total ?? 0;

  const [rawSelection, setSelection] = useState<SelectionState<LeadRow>>(() => emptySelection(filterKey));
  const selection = forFilter(rawSelection, filterKey);
  const selCount = selectedCount(selection, total);
  const clearSelection = () => setSelection(emptySelection(filterKey));

  const [dialog, setDialog] = useState<DialogId>(null);
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);
  const openIndex = rows.findIndex((r) => r.key === openRowKey);
  const openRow = openIndex >= 0 ? rows[openIndex]! : null;

  const mark = useMarkLeads(folder.publicId);
  const remove = useRemoveLeads(folder.publicId);
  const refresh = useRefreshStatuses(folder.publicId);
  const updateLead = useUpdateLead(folder.publicId);
  const csv = useExportCsv();
  const importCsv = useImportCsv();
  const fileRef = useRef<HTMLInputElement>(null);
  const [collecting, setCollecting] = useState(false);

  const status = folderStatus(folder);
  const leadTotal = totalLeadCount(folder);
  const owner = ownerOf(folder);
  const platform = useMemo(() => majorityPlatform(rows), [rows]);

  // ── Targets for bulk actions ─────────────────────────────────────────────
  function rowsTarget(list: LeadRow[]): BulkTarget {
    return {
      kind: 'rows',
      count: list.length,
      usernames: list.map((r) => r.handleKey),
      leadIds: list.flatMap((r) => (r.leadId != null ? [r.leadId] : [])),
      creatorIds: list.map((r) => r.creatorId),
    };
  }
  const selectionTarget: BulkTarget =
    selCount === 0
      ? { kind: 'all', count: tab === 'leads' && leadsQ.data ? total : leadTotal, filter: tab === 'leads' ? filter : {} }
      : selection.mode === 'all'
        ? { kind: 'all', count: total, filter }
        : rowsTarget([...selection.rows.values()]);
  const [sheetTarget, setSheetTarget] = useState<BulkTarget | null>(null);
  const sendTarget = sheetTarget ?? selectionTarget;
  const openLeadsTarget: BulkTarget = { kind: 'all', count: openLeadCount(folder), filter: { done: 'no' } };

  /** Usernames for endpoints that only take explicit handles, fetching every page in "all" mode. */
  async function selectedHandles(): Promise<string[]> {
    if (selection.mode === 'rows') return [...selection.rows.values()].map((r) => r.handleKey);
    setCollecting(true);
    try {
      const all = await collectAll((page, limit) => client.workspace.leadsPage(folder.publicId, { ...filter, page, limit }));
      return all.map((l) => fromLead(l).handleKey);
    } finally {
      setCollecting(false);
    }
  }

  async function runMark(action: LeadMarkAction, handles?: string[]) {
    try {
      const usernames = handles ?? (await selectedHandles());
      if (usernames.length === 0) return;
      const res = await mark.mutateAsync({ usernames, action });
      toast.success(`${res.updated.toLocaleString()} lead${res.updated === 1 ? '' : 's'} ${MARK_DONE_TEXT[action]}`);
      if (!handles) clearSelection();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update those leads");
    }
  }

  async function runRemove(handles?: string[]) {
    try {
      const usernames = handles ?? (await selectedHandles());
      if (usernames.length === 0) return;
      const res = await remove.mutateAsync(usernames);
      toast.success(`Removed ${res.deleted.toLocaleString()} from this folder`);
      if (!handles) clearSelection();
      setOpenRowKey(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't remove those leads");
    }
  }

  async function refreshAll() {
    try {
      setCollecting(true);
      const all = await collectAll((page, limit) => client.workspace.leadsPage(folder.publicId, { page, limit }));
      const res = await refresh.mutateAsync(all.map((l) => fromLead(l).handleKey));
      toast.success(`Checked ${all.length.toLocaleString()} leads`, { description: `${res.updated} changed status.` });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't refresh statuses");
    } finally {
      setCollecting(false);
    }
  }

  function jobStarted(res: { folder?: { publicId: string; name: string; isNew: boolean } }, message: string) {
    setDialog(null);
    void qc.invalidateQueries({ queryKey: foldersKey });
    void qc.invalidateQueries({ queryKey: activeJobKey(folder.publicId) });
    if (res.folder?.isNew) {
      void qc.invalidateQueries({ queryKey: activeJobKey(res.folder.publicId) });
      navigate(paths.folder(res.folder.publicId, 'find'));
      toast.success(`${message} into "${res.folder.name}"`);
    } else {
      navigate(paths.folder(folder.publicId, 'find'));
      toast.success(`${message} into this folder`, { description: 'Progress shows on the Find creators tab.' });
    }
  }

  const busy = mark.isPending || remove.isPending || collecting;
  const bulkActions: BulkAction[] = [
    { label: 'Send to outreach', icon: Send, onClick: () => setDialog('send') },
    { label: 'Reassign…', icon: UserRoundCog, onClick: () => setDialog('reassign') },
    { label: 'Mark irrelevant', icon: Ban, onClick: () => void runMark('irrelevant'), disabled: busy },
    { label: 'Mark complete', icon: CheckCheck, onClick: () => void runMark('complete'), disabled: busy },
    { label: 'Reopen', icon: RotateCcw, onClick: () => void runMark('reopen'), disabled: busy },
    { label: 'Remove', icon: Trash2, destructive: true, onClick: () => setDialog('remove'), disabled: busy },
  ];

  const sheetActions: SheetAction[] = openRow
    ? [
        {
          label: 'Send to outreach',
          icon: Send,
          primary: true,
          onClick: () => {
            setSheetTarget(rowsTarget([openRow]));
            setDialog('send');
          },
        },
        {
          label: 'Reassign…',
          icon: UserRoundCog,
          onClick: () => {
            setSheetTarget(rowsTarget([openRow]));
            setDialog('reassign');
          },
        },
        openRow.doneReason
          ? { label: 'Reopen', icon: RotateCcw, disabled: busy, onClick: () => void runMark('reopen', [openRow.handleKey]) }
          : { label: 'Mark irrelevant', icon: Ban, disabled: busy, onClick: () => void runMark('irrelevant', [openRow.handleKey]) },
        ...(openRow.doneReason
          ? []
          : [{ label: 'Mark complete', icon: CheckCheck, disabled: busy, onClick: () => void runMark('complete', [openRow.handleKey]) }]),
        { label: 'Remove', icon: Trash2, destructive: true, disabled: busy, onClick: () => void runRemove([openRow.handleKey]) },
      ]
    : [];

  const closeDialog = (open: boolean) => {
    if (!open) {
      setDialog(null);
      setSheetTarget(null);
    }
  };

  const archivedNote =
    status === 'archived' ? (
      <Note
        tone={folder.archivedReason === 'auto' ? 'info' : 'warning'}
        action={
          <Button size="sm" variant="outline" disabled={menu.isPending} onClick={menu.restore}>
            <ArchiveRestore className="h-3.5 w-3.5" />
            Restore
          </Button>
        }
      >
        {folder.archivedReason === 'auto' && folder.autoArchivedAt
          ? `Archived automatically on ${formatDate(folder.autoArchivedAt)}, ${archiveAfterDays} days after every lead was done. Its schedules are paused.`
          : `Archived${folder.archivedAt ? ` on ${formatDate(folder.archivedAt)}` : ''}. Its schedules are paused.`}
      </Note>
    ) : null;
  const archivesIn = archivesInLabel(archivesInDays(folder, archiveAfterDays));
  const outcomes = useOutcomes();
  const quality = folderQualityNotice(folder) ?? outcomes.qualityByFolder.get(folder.publicId) ?? null;

  return (
    <div className="space-y-5">
      <header className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <FolderDot folder={folder} className="h-2.5 w-2.5" />
              <h1 className="min-w-0 truncate text-[26px] font-bold tracking-[-0.03em]" title={folder.name}>
                {folder.name}
              </h1>
              <StatusPill status={status} />
              <Pill tone="muted" icon={Tags} title="What this folder's creators are for. Change it under Folder actions.">
                {FOLDER_PURPOSE_LABELS[folderPurpose(folder)]}
              </Pill>
            </div>
            <p className="flex flex-wrap gap-x-2 text-sm text-muted-foreground">
              {[
                owner ? `Owned by ${owner}` : null,
                platform ? PLATFORM_LABELS[platform] : null,
                `${leadTotal.toLocaleString()} lead${leadTotal === 1 ? '' : 's'}`,
                `Created ${formatDate(folder.createdAt)}`,
                archivesIn,
              ]
                .filter(Boolean)
                .map((part, i) => (
                  <span key={i}>{i > 0 ? `· ${part}` : part}</span>
                ))}
            </p>
            {folder.description && <p className="max-w-[65ch] text-sm text-muted-foreground">{folder.description}</p>}
            <OutcomeChips outcome={outcomes.byFolder.get(folder.publicId)} className="pt-0.5" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <MenuButton
              label="Find creators"
              icon={Search}
              items={[
                { label: 'Search', icon: Search, onSelect: () => navigate(paths.folder(folder.publicId, 'find')) },
                { label: 'Lookalikes', icon: Sparkles, onSelect: () => setDialog('lookalike'), hint: 'TikTok creators like the ones in this folder' },
                { label: 'Reposts', icon: Repeat2, onSelect: () => setDialog('reposts'), hint: 'Creators this folder’s TikTok creators repost' },
                { label: 'Add manually', icon: UserPlus, onSelect: () => setDialog('add') },
                { label: 'Import CSV', icon: Upload, onSelect: () => fileRef.current?.click(), disabled: importCsv.isPending },
              ]}
            />
            <PresenterCheckButton folderId={folder.publicId} />
            <Button size="sm" variant="outline" disabled={leadTotal === 0} onClick={() => setDialog('send')}>
              <Send className="h-3.5 w-3.5" />
              {selCount > 0 ? `Send ${selCount.toLocaleString()} to outreach` : 'Send to outreach'}
            </Button>
            <RowActions
              triggerLabel="Folder actions"
              items={[
                { label: 'Refresh status', icon: RefreshCw, onClick: () => void refreshAll(), disabled: leadTotal === 0 || collecting },
                { label: 'Export CSV', icon: Download, onClick: () => csv.mutate(folder.publicId), disabled: leadTotal === 0 },
                { label: 'Reassign all open leads…', icon: UserRoundCog, onClick: () => setDialog('reassignOpen'), disabled: openLeadCount(folder) === 0 },
                menu.items.rename,
                menu.items.purpose,
                status === 'archived'
                  ? { label: 'Restore', icon: ArchiveRestore, onClick: menu.restore }
                  : { label: 'Archive', icon: Archive, onClick: menu.items.archive.onClick },
                menu.items.delete,
              ]}
            />
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(ev) => {
                const file = ev.target.files?.[0];
                ev.target.value = '';
                if (!file) return;
                importCsv.mutate(
                  { folderPublicId: folder.publicId, file },
                  {
                    onSuccess: (r) => toast.success(`Imported ${r.saved} lead${r.saved === 1 ? '' : 's'}`),
                    onError: (err) => toast.error((err as Error).message),
                  },
                );
              }}
            />
          </div>
        </div>
        {archivedNote}
        <SearchQualityNotice notice={quality} findHref={paths.folder(folder.publicId, 'find')} />
        <FolderProgressBar counts={folder.counts} />
        <nav role="tablist" aria-label="Folder" className="flex flex-wrap gap-1 border-b border-border">
          {FOLDER_TABS.map((t) => (
            <Link
              key={t}
              to={paths.folder(folder.publicId, t)}
              role="tab"
              aria-selected={tab === t}
              className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
                tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {FOLDER_TAB_LABELS[t]}
              {t === 'leads' && leadTotal > 0 && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-xs leading-none text-muted-foreground">
                  {leadTotal.toLocaleString()}
                </span>
              )}
            </Link>
          ))}
        </nav>
      </header>

      <div role="tabpanel" aria-label={FOLDER_TAB_LABELS[tab]}>
        {tab === 'leads' && (
          <div className="space-y-4">
            <LeadFilters state={url} onChange={setUrl} />
            <LeadTable
              scope="folder"
              tableId="discovery-folder-leads-v1"
              rows={rows}
              total={total}
              loading={leadsQ.isFetching}
              error={leadsQ.error as Error | null}
              page={url.page}
              limit={LEADS_PAGE_SIZE}
              onPageChange={(page) => setUrl({ page })}
              sort={url.sort}
              onSortChange={(sort) => setUrl({ sort, page: 1 })}
              selection={selection}
              onSelectionChange={setSelection}
              onOpenRow={(row) => setOpenRowKey(row.key)}
              bulkActions={<BulkActions actions={bulkActions} />}
              emptyTitle={leadTotal === 0 ? 'No leads yet' : 'No leads match these filters'}
              emptyHint={leadTotal === 0 ? 'Use Find creators to fill this folder.' : 'Try widening the filters.'}
            />
          </div>
        )}
        {tab === 'find' && (
          <SearchPanel folderPublicId={folder.publicId} folderIsEmpty={leadTotal === 0} folderName={folder.name} />
        )}
        {tab === 'schedules' && <SchedulesTab folderPublicId={folder.publicId} />}
        {tab === 'briefing' && <FolderGuidancePanel folderPublicId={folder.publicId} alwaysOpen />}
        {tab === 'activity' && <FolderActivity folderPublicId={folder.publicId} folderName={folder.name} />}
      </div>

      <CreatorSheet
        row={openRow}
        open={!!openRow}
        onOpenChange={(o) => !o && setOpenRowKey(null)}
        onPrev={openIndex > 0 ? () => setOpenRowKey(rows[openIndex - 1]!.key) : undefined}
        onNext={openIndex >= 0 && openIndex < rows.length - 1 ? () => setOpenRowKey(rows[openIndex + 1]!.key) : undefined}
        actions={sheetActions}
        savingNotes={updateLead.isPending}
        onSaveNotes={(notes) =>
          openRow &&
          updateLead.mutate(
            { username: openRow.handleKey, notes },
            { onSuccess: () => toast.success('Notes saved'), onError: (err) => toast.error((err as Error).message) },
          )
        }
      />

      <SendToOutreachDialog
        open={dialog === 'send'}
        onOpenChange={closeDialog}
        scope={{ kind: 'folder', folderPublicId: folder.publicId }}
        target={sendTarget}
        onDone={sheetTarget ? undefined : clearSelection}
      />
      <ReassignDialog
        open={dialog === 'reassign'}
        onOpenChange={closeDialog}
        scope={{ kind: 'folder', folderPublicId: folder.publicId }}
        target={sheetTarget ?? (selCount > 0 ? selectionTarget : { kind: 'all', count: total, filter })}
        onDone={sheetTarget ? undefined : clearSelection}
      />
      <ReassignDialog
        open={dialog === 'reassignOpen'}
        onOpenChange={closeDialog}
        scope={{ kind: 'folder', folderPublicId: folder.publicId }}
        target={openLeadsTarget}
        title={`Reassign all ${openLeadsTarget.count.toLocaleString()} open leads`}
      />
      <ConfirmDialog
        open={dialog === 'remove'}
        onOpenChange={closeDialog}
        tone="destructive"
        title={`Remove ${selCount.toLocaleString()} lead${selCount === 1 ? '' : 's'} from this folder?`}
        description="They stay in any other folders and in outreach. Marking them irrelevant keeps the record instead."
        confirmLabel="Remove"
        pending={busy}
        onConfirm={() => runRemove()}
      />
      {menu.dialogs}
      {dialog === 'lookalike' && (
        <LookalikeDialog folder={folder} onClose={() => setDialog(null)} onStarted={(res) => jobStarted(res, 'Lookalike search running')} />
      )}
      {dialog === 'reposts' && (
        <RepostDialog folder={folder} onClose={() => setDialog(null)} onStarted={(res) => jobStarted(res, 'Adding reposted creators')} />
      )}
      {dialog === 'add' && (
        <AddCreatorsDialog
          folder={folder}
          onClose={() => setDialog(null)}
          onStarted={(res) => jobStarted({}, `Adding ${res.count} creator${res.count === 1 ? '' : 's'}`)}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: ReturnType<typeof folderStatus> }): JSX.Element {
  if (status === 'complete')
    return (
      <Pill tone="success" icon={CheckCircle2}>
        {FOLDER_STATUS_LABELS.complete}
      </Pill>
    );
  if (status === 'archived')
    return (
      <Pill tone="muted" icon={Archive}>
        {FOLDER_STATUS_LABELS.archived}
      </Pill>
    );
  return (
    <Pill tone="info" icon={Activity}>
      {FOLDER_STATUS_LABELS.active}
    </Pill>
  );
}

function SchedulesTab({ folderPublicId }: { folderPublicId: string }): JSX.Element {
  const { data, isLoading } = useSchedules(folderPublicId);
  const paths = useDiscoveryPaths();
  if (!isLoading && (data ?? []).length === 0) {
    return (
      <div className="table-frame">
        <EmptyState
          icon={<CalendarClock className="h-6 w-6" strokeWidth={1.5} />}
          title="No schedules"
          hint="Run a search from Find creators and choose to repeat it, and it shows up here."
        />
        <p className="pb-6 text-center">
          <Link to={paths.folder(folderPublicId, 'find')} className="text-sm font-semibold text-brand hover:underline">
            Go to Find creators
          </Link>
        </p>
      </div>
    );
  }
  return <SchedulePanel folderPublicId={folderPublicId} />;
}

function majorityPlatform(rows: readonly LeadRow[]): Platform | null {
  if (rows.length === 0) return null;
  const counts = new Map<Platform, number>();
  for (const r of rows) counts.set(r.platform, (counts.get(r.platform) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
}
